/* eslint-disable tailwindcss/no-custom-classname */

import classNames from 'classnames';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { SkuT } from '@lambda/apis/src/catalog-v2/types';
import Link from 'next/link';
import { logger } from '@/lib/logger';

import { defaultBreakpointsV2 } from '@/lib/common/breakpoints';
import settings from '@/settings';
import i18n from '@/settings/i18n';

import { CUSTOMER_FAVORITES } from './constants';
import Slider from '@/components/slider/Slider';
import { CategoryItemT } from './types';
import { generateSavingsPercentage } from '@/components/v2/product/hooks/helpers';
import OfferCard from '@/components/v2/common/card/OfferCard';
import { generateOfferImage } from '@/components/v2/product/sections/main/left/image/helpers';
import Section from '@/components/commons/Section';

const t = i18n.init('homepage');

const Placeholder = () => (
  <Slider breakpoints={defaultBreakpointsV2} speed={300}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="h-[265px] animate-pulse border border-[#F9F9F9] bg-gray-500"
      />
    ))}
  </Slider>
);

const CustomerFavorites = ({
  isThankYouPage = false,
}: {
  isThankYouPage?: boolean;
}) => {
  const categories = CUSTOMER_FAVORITES?.[settings.store] as CategoryItemT[];

  const [activeCategory, setActiveCategory] = useState<CategoryItemT | null>(
    categories?.[0] ?? null,
  );
  const [favoritesByCategory, setFavoritesByCategory] = useState<
    Record<string, SkuT[] | null>
  >({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeCategory) {
      return;
    }

    const categoryKey = String(activeCategory.value);

    if (favoritesByCategory[categoryKey]) {
      setLoading(false);

      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const loadFavorites = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `/api/catalog-v2/skus?categoryPks=${activeCategory.categoryPk}&limit=24&includeFilters=false`,
          { signal },
        );

        if (!res.ok) {
          throw new Error('Failed to fetch');
        }

        const data = await res.json();

        if (!signal.aborted) {
          setFavoritesByCategory((prev) => ({
            ...prev,
            [categoryKey]: data.items || [],
          }));
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          logger.info('CustomerFavorites request aborted');
        } else {
          logger.error({ err }, 'Failed to load CustomerFavorites category');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadFavorites();

    // eslint-disable-next-line consistent-return
    return () => controller.abort();
  }, [activeCategory?.value]);

  if (!activeCategory || !categories || categories.length === 0) {
    return null;
  }

  const productsForActiveCategory =
    favoritesByCategory[activeCategory.value] || null;

  return (
    <div className="reebelo-container">
      <Section
        className={classNames({ 'pb-6 xxs:pb-8 md:pb-10': isThankYouPage })}
      >
        <div className="hide-scrollbar text-sm">
          <div className="flex flex-wrap items-center justify-between gap-x-4">
            <h2 className="flex shrink-0 items-center text-lg font-bold md:text-xl">
              {t`Customer Favorites`}
            </h2>

            <Link
              href={`/collections/${activeCategory.value}/${activeCategory.categoryPk}`}
              className="flex shrink-0 items-center justify-center gap-2 hover:underline lg:hidden"
            >
              {t`See all`}{' '}
              <ArrowRight className="h-4 w-4 shrink-0" strokeWidth={2} />
            </Link>
          </div>

          <div className="mt-2 flex w-full items-center justify-between gap-4 lg:mt-4">
            <div className="hide-scrollbar flex flex-1 shrink-0 gap-1 overflow-x-auto">
              {categories.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveCategory(item)}
                  className={classNames(
                    'h-8 shrink-0 rounded-full border px-4 text-xs transition duration-150 ease-in-out focus:outline-none',
                    {
                      'border-gray-700 bg-gray-700 text-white':
                        activeCategory.value === item.value,
                      'border-gray-500 bg-white hover:border-gray-700 hover:bg-gray-700 hover:text-white':
                        activeCategory.value !== item.value,
                    },
                  )}
                >
                  {t(item.name)}
                </button>
              ))}
            </div>

            <Link
              href={`/collections/${activeCategory.value}/${activeCategory.categoryPk}`}
              className="hidden shrink-0 items-center justify-center gap-2 font-semibold hover:underline lg:flex"
            >
              {t`See all`}{' '}
              <ArrowRight className="h-4 w-4 shrink-0" strokeWidth={2.5} />
            </Link>
          </div>

          <div className="relative mt-4">
            {!loading &&
            productsForActiveCategory &&
            productsForActiveCategory.length > 0 ? (
              <Slider breakpoints={defaultBreakpointsV2} speed={300}>
                {productsForActiveCategory.map((sku, index) => {
                  const offer = sku?.offers?.[0];
                  const skuProduct = sku?.product;

                  if (!offer || !sku || !skuProduct) {
                    return null;
                  }

                  const compareAtPrice = sku.compareAtPrice ?? null;

                  const percentageSaving = generateSavingsPercentage(
                    offer.price,
                    compareAtPrice,
                  );

                  const isFeatured = !!offer?.featured;

                  return (
                    <OfferCard
                      index={index}
                      key={`customer-favorites-${sku.id}`}
                      link={`/collections/${encodeURIComponent(
                        skuProduct.slug,
                      )}?skuId=${encodeURIComponent(sku.id)}`}
                      image={generateOfferImage(sku, skuProduct)}
                      title={sku.title}
                      compareAtPrice={
                        compareAtPrice ? compareAtPrice / 100 : null
                      }
                      percentageSaving={percentageSaving}
                      price={offer.price / 100}
                      className="!border-[#F9F9F9]"
                      featured={isFeatured}
                    />
                  );
                })}
              </Slider>
            ) : (
              <Placeholder />
            )}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CustomerFavorites;
